// src/api/googlecalendar.ts
import api from "./client";
import { config } from "../config";

export type CalendarProvider = "google" | "microsoft" | null;

export type BusinessCalendar = {
  business_calendar_id: string;
  provider: "google" | "microsoft";
  provider_calendar_id: string;
  calendar_name: string;
  timezone: string | null;
  color: string | null;
  is_bookable: boolean;
  is_default: boolean;
};

/**
 * --------------------------------------------------------------------
 *  Get/update business calendar connection details
 * --------------------------------------------------------------------
 */
export async function calendarDetails(
  body?: { provider: "google" | "microsoft"; connected: boolean }
) {
  const url = "/calendar_details";

  // 🚨 SILENTLY PREVENT BAD SHAPE (strings, arrays, numbers)
  if (body && (typeof body !== "object" || Array.isArray(body))) {
    console.error("❌ calendarDetails() invalid body:", body);
    throw new Error("calendarDetails body must be an object with provider/connected");
  }

  try {
    const response = body
      ? await api.post(url, body)  // update
      : await api.get(url);        // fetch

    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching calendar details:", error);
    throw error?.response?.data || error;
  }
}

/**
 * --------------------------------------------------------------------
 *  Step 1 — Launch Google OAuth flow
 * --------------------------------------------------------------------
 */
export function connectGoogleCalendar(state?: string) {
  const params = new URLSearchParams({
    client_id: config.google.clientId,
    redirect_uri: config.google.redirectUri,
    response_type: "code",
    access_type: "offline",
    prompt: "consent",
    include_granted_scopes: "true",
    scope: [
      "https://www.googleapis.com/auth/calendar.readonly",
      "https://www.googleapis.com/auth/calendar.events",
    ].join(" "),
  });

  if (state) params.set("state", state);

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * --------------------------------------------------------------------
 *  Step 2 — Complete Google connect
 * --------------------------------------------------------------------
 */
export async function completeGoogleConnect(code: string) {
  try {
    const res = await api.post("/connect_calendar", {
      provider: "google",
      code,
    });

    return res.data;
  } catch (err: any) {
    console.error("❌ Error completing Google connect:", err);
    throw err?.response?.data || err;
  }
}

/**
 * --------------------------------------------------------------------
 *  Disconnect Google Calendar
 * --------------------------------------------------------------------
 */
export async function disconnectGoogleCalendar() {
  try {
    const res = await api.post("/disconnect_calendar", {
      provider: "google",
    });

    return res.data;
  } catch (err: any) {
    console.error("❌ Error disconnecting Google calendar:", err);
    throw err?.response?.data || err;
  }
}

/**
 * --------------------------------------------------------------------
 * Fetch all business calendars
 * --------------------------------------------------------------------
 */
export async function fetchBusinessCalendars(): Promise<BusinessCalendar[]> {
  try {
    const res = await api.get("/business_calendars");
    return Array.isArray(res.data) ? res.data : [];
  } catch (err: any) {
    console.error("❌ Error fetching business calendars:", err);
    throw err?.response?.data || err;
  }
}

/**
 * --------------------------------------------------------------------
 * Update calendar (bookable/default)
 * --------------------------------------------------------------------
 */
export async function updateBusinessCalendar(
  businessCalendarId: string,
  patch: Partial<Pick<BusinessCalendar, "is_bookable" | "is_default">>
) {
  try {
    const res = await api.patch(`/business_calendars/${businessCalendarId}`, patch);
    return res.data;
  } catch (err: any) {
    console.error("❌ Error updating business calendar:", err);
    throw err?.response?.data || err;
  }
}
